import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './BeautifulGlassDashboard.css';

interface DashboardMetrics {
  overview: {
    total_emails_sent: number;
    total_leads: number;
    total_campaigns: number;
    interested_lead_replies: number;
    first_rep_replies: number;
    missed_opportunities: number;
    avg_response_time_hours: number;
    median_response_time_hours: number;
  };
  response_times: {
    distribution: {
      under_1_hour: number;
      '1_to_4_hours': number;
      '4_to_8_hours': number;
      '8_to_24_hours': number;
      '1_to_3_days': number;
      over_3_days: number;
    };
    total_responses: number;
  };
  rep_performance: {
    individual_performance: {
      [key: string]: {
        total_responses: number;
        avg_response_time_hours: number;
        quick_response_rate_percent: number;
        median_response_time_hours: number;
      };
    };
  };
}

interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  className?: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, description, className = '' }) => (
  <div className={`glass-card kpi-card ${className}`}>
    <div className="card-icon-container">
      <div className="card-icon">
        {icon}
      </div>
    </div>
    <div className="card-content">
      <div className="card-value">{value}</div>
      <div className="card-title">{title}</div>
      {description && <div className="card-description">{description}</div>}
    </div>
    <div className="card-glow"></div>
  </div>
);

const ResponseTimeChart: React.FC<{ data: DashboardMetrics['response_times'] }> = ({ data }) => {
  const chartData = [
    { name: '< 1h', value: data.distribution.under_1_hour, percentage: ((data.distribution.under_1_hour / data.total_responses) * 100).toFixed(1) },
    { name: '1-4h', value: data.distribution['1_to_4_hours'], percentage: ((data.distribution['1_to_4_hours'] / data.total_responses) * 100).toFixed(1) },
    { name: '4-8h', value: data.distribution['4_to_8_hours'], percentage: ((data.distribution['4_to_8_hours'] / data.total_responses) * 100).toFixed(1) },
    { name: '8-24h', value: data.distribution['8_to_24_hours'], percentage: ((data.distribution['8_to_24_hours'] / data.total_responses) * 100).toFixed(1) },
    { name: '1-3d', value: data.distribution['1_to_3_days'], percentage: ((data.distribution['1_to_3_days'] / data.total_responses) * 100).toFixed(1) },
    { name: '3+ days', value: data.distribution.over_3_days, percentage: ((data.distribution.over_3_days / data.total_responses) * 100).toFixed(1) },
  ];

  return (
    <div className="glass-card chart-container">
      <h3 className="chart-title">Response Time Distribution</h3>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="50%" stopColor="#60a5fa" />
                <stop offset="100%" stopColor="#93c5fd" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(156, 163, 175, 0.3)" />
            <XAxis 
              dataKey="name" 
              tick={{ fill: '#374151', fontSize: 12, fontWeight: 500 }} 
              axisLine={{ stroke: '#9ca3af' }}
              tickLine={{ stroke: '#9ca3af' }}
            />
            <YAxis 
              tick={{ fill: '#374151', fontSize: 12, fontWeight: 500 }} 
              axisLine={{ stroke: '#9ca3af' }}
              tickLine={{ stroke: '#9ca3af' }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255,255,255,0.95)', 
                border: 'none', 
                borderRadius: '16px',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 8px 32px rgba(31, 38, 135, 0.37)',
                color: '#1f2937',
                fontWeight: 500
              }}
              formatter={(value, name) => [`${value} responses (${chartData.find(d => d.value === value)?.percentage}%)`, 'Count']}
            />
            <Bar 
              dataKey="value" 
              fill="url(#barGradient)"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const RepLeaderboard: React.FC<{ data: DashboardMetrics['rep_performance'] }> = ({ data }) => {
  const reps = Object.entries(data.individual_performance)
    .map(([name, performance]) => ({
      name,
      ...performance,
      score: (performance.quick_response_rate_percent * 0.6) + ((200 - Math.min(performance.avg_response_time_hours, 200)) / 200 * 40)
    }))
    .sort((a, b) => b.score - a.score);

  const formatTime = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)}min`;
    if (hours < 24) return `${hours.toFixed(1)}h`;
    return `${Math.round(hours / 24)}d`;
  };

  return (
    <div className="glass-card leaderboard-container">
      <h3 className="chart-title">Rep Performance Leaderboard</h3>
      <div className="leaderboard-list">
        {reps.map((rep, index) => (
          <div key={rep.name} className={`leaderboard-item ${index === 0 ? 'top-performer' : ''}`}>
            <div className="rep-rank-section">
              <div className={`rep-rank ${index < 3 ? `rank-${index + 1}` : ''}`}>
                {index === 0 && <span className="crown-emoji">👑</span>}
                <span className="rank-number">{index + 1}</span>
              </div>
              <div className="rep-info">
                <div className="rep-name">{rep.name}</div>
                <div className="rep-stats">{rep.total_responses} responses</div>
              </div>
            </div>
            <div className="rep-performance">
              <div className="performance-metric">
                <div className="metric-value">{formatTime(rep.avg_response_time_hours)}</div>
                <div className="metric-label">avg time</div>
              </div>
              <div className="performance-metric">
                <div className="metric-value">{rep.quick_response_rate_percent.toFixed(1)}%</div>
                <div className="metric-label">avg reply rate</div>
              </div>
            </div>
            {index === 0 && <div className="performance-glow"></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const BeautifulGlassDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call our secure API instead of static mock data
      const response = await fetch('https://instantly-analytics-api-dev.theo-213.workers.dev/api/dashboard', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to load dashboard data');
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to load dashboard data');
      }
      
      // Transform API data to match the expected format
      const apiData = result.data;
      const transformedData = {
        overview: {
          total_emails_sent: apiData.summary.total_emails,
          total_leads: apiData.summary.total_leads,
          total_campaigns: apiData.summary.total_campaigns,
          interested_lead_replies: apiData.summary.high_interest_conversations,
          first_rep_replies: apiData.summary.total_conversations,
          missed_opportunities: 0,
          avg_response_time_hours: apiData.summary.avg_response_time_hours,
          median_response_time_hours: apiData.summary.avg_response_time_hours
        },
        response_times: {
          distribution: {
            under_1_hour: 0,
            '1_to_4_hours': 0,
            '4_to_8_hours': 0,
            '8_to_24_hours': 0,
            '1_to_3_days': 0,
            over_3_days: 0
          },
          total_responses: apiData.summary.total_conversations
        },
        rep_performance: {
          individual_performance: {}
        }
      };
      
      setDashboardData(transformedData);
    } catch (err) {
      console.error('Dashboard API error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="glass-card loading-card">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading dashboard...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-container">
          <div className="glass-card error-card">
            <span className="error-emoji">❌</span>
            <h2 className="error-title">Error Loading Dashboard</h2>
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={loadDashboardData}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="glass-card loading-card">
            <div className="loading-text">No data available</div>
          </div>
        </div>
      </div>
    );
  }

  const interestedReplyRate = ((dashboardData.overview.interested_lead_replies / dashboardData.overview.total_emails_sent) * 100).toFixed(2);
  const responseCoverage = ((dashboardData.overview.first_rep_replies / dashboardData.overview.interested_lead_replies) * 100).toFixed(1);

  return (
    <div className="dashboard-container">
      {/* Animated Background */}
      <div className="animated-background"></div>
      
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-info">
            <h1 className="dashboard-title">Instantly.ai Performance Dashboard</h1>
            <p className="dashboard-subtitle">
              Last Updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <button className="refresh-button" onClick={loadDashboardData}>
            <RefreshCw className="refresh-icon" />
            <span>Refresh</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* API Data Section */}
        <section className="api-data-section">
          <div className="section-header">
            <h2 className="section-title">📊 Raw Data from Instantly.ai API</h2>
            <p className="section-description">Direct metrics pulled from your Instantly.ai campaigns</p>
          </div>
          <div className="api-metrics-grid">
            <KPICard
              title="Total Leads"
              value={dashboardData.overview.total_leads.toLocaleString()}
              icon={<span className="emoji-icon">👥</span>}
              description="Total leads across all campaigns"
              className="api-card"
            />
            <KPICard
              title="Total Campaigns"
              value={dashboardData.overview.total_campaigns.toLocaleString()}
              icon={<span className="emoji-icon">🚀</span>}
              description="Active and completed campaigns"
              className="api-card"
            />
            <KPICard
              title="Total Emails Sent"
              value={dashboardData.overview.total_emails_sent.toLocaleString()}
              icon={<span className="emoji-icon">📧</span>}
              description="Total outreach emails delivered"
              className="api-card"
            />
            <KPICard
              title="Interested Lead Replies"
              value={dashboardData.overview.interested_lead_replies.toLocaleString()}
              icon={<span className="emoji-icon">💬</span>}
              description="Leads showing positive interest"
              className="api-card"
            />
          </div>
        </section>

        {/* Calculated Insights Section */}
        <section className="insights-section">
          <div className="section-header">
            <h2 className="section-title">🧮 Calculated Performance Insights</h2>
            <p className="section-description">Our analysis of your campaign performance and response patterns</p>
          </div>
          <div className="insights-metrics-grid">
            <KPICard
              title="First Rep Replies"
              value={dashboardData.overview.first_rep_replies.toLocaleString()}
              icon={<span className="emoji-icon">💼</span>}
              description="Initial responses from sales reps"
              className="insight-card"
            />
            <KPICard
              title="Missed Opportunities"
              value={dashboardData.overview.missed_opportunities}
              icon={<span className="emoji-icon">⚠️</span>}
              description="Interested leads with no response"
              className="insight-card"
            />
            <KPICard
              title="Avg Response Time"
              value={`${dashboardData.overview.avg_response_time_hours.toFixed(1)}h`}
              icon={<span className="emoji-icon">⏱️</span>}
              description={`Median: ${dashboardData.overview.median_response_time_hours.toFixed(1)}h`}
              className="insight-card"
            />
            <KPICard
              title="Response Rate"
              value={`${responseCoverage}%`}
              icon={<span className="emoji-icon">📈</span>}
              description={`${dashboardData.overview.first_rep_replies} of ${dashboardData.overview.interested_lead_replies} interested leads`}
              className="insight-card"
            />
          </div>
        </section>

        {/* Performance Analysis Section */}
        <section className="analysis-section">
          <div className="section-header">
            <h2 className="section-title">📊 Performance Analysis</h2>
            <p className="section-description">Detailed breakdown of response patterns and team performance</p>
          </div>
          <div className="charts-section">
            <ResponseTimeChart data={dashboardData.response_times} />
            <RepLeaderboard data={dashboardData.rep_performance} />
          </div>
        </section>
      </main>
    </div>
  );
};